import type { StateCreator } from "zustand";
import {
  ExcludeBroadcastClips,
  ExcludePodcastEpisodes,
} from "@/data-access/discovery-view/queries/discovery-view-queries";
import type { FeedArticleProjectionData } from "@/data-access/shared/fragments/feed-article-projection-fragments";
import { client } from "@/lib/urqlProvider";
import type {
  DiscoveryArticlesTableModalState,
  DiscoveryArticlesTableSlice,
  DiscoveryBroadcastClipsTableSlice,
  DiscoveryNarrativeClustersTableSlice,
  DiscoveryPodcastEpisodesTableSlice,
} from "./discovery-content-management.slice";

export interface ExcludeItemReasons {
  notRelevant?: boolean;
  other?: boolean;
  otherReason?: string;
}

export interface ExcludeItemCallbacks {
  onSuccess: () => void;
}

type DiscoveryContentManagementActions = {
  excludeBroadcastClips: (
    feedId: string,
    reasons: ExcludeItemReasons,
    callbacks?: ExcludeItemCallbacks,
  ) => Promise<{ error: string | null }>;

  excludePodcastEpisodes: (
    feedId: string,
    reasons: ExcludeItemReasons,
    callbacks?: ExcludeItemCallbacks,
  ) => Promise<{ error: string | null }>;
} & Pick<
  DiscoveryArticlesTableSlice,
  | "setInitialLoading"
  | "setFetchingArticles"
  | "setError"
  | "setTotalCount"
  | "setSearchTerm"
  | "setIsAdvancedSearch"
  | "resetState"
> &
  Pick<
    DiscoveryNarrativeClustersTableSlice,
    | "setIsNarrativeClustersInitialLoading"
    | "setIsFetchingNarrativeClustersTableData"
    | "setNarrativeClustersTableError"
    | "setTotalNarrativeClustersCount"
    | "setNarrativeClustersSearchTerm"
    | "resetNarrativeClustersTableState"
  > &
  Pick<
    DiscoveryArticlesTableModalState,
    | "setIsAddArticleModalOpen"
    | "setIsAddingArticle"
    | "resetAddArticleModalState"
    | "setPreviewArticle"
  > &
  Pick<
    DiscoveryBroadcastClipsTableSlice,
    | "setIsFetchingBroadcastClips"
    | "setBroadcastClipsError"
    | "setTotalBroadcastClipsCount"
    | "setIsBroadcastClipsInitialLoading"
    | "setBroadcastClipsSearchTerm"
    | "resetBroadcastClipsTableState"
    | "setIsBroadcastExcludeModalOpen"
    | "setPendingBroadcastExclusionIds"
    | "resetBroadcastExcludeModalState"
    | "setSelectedBroadcastClipIds"
    | "setBroadcastExcludeSuccessCallback"
  > &
  Pick<
    DiscoveryPodcastEpisodesTableSlice,
    | "setIsFetchingPodcastEpisodes"
    | "setPodcastEpisodesError"
    | "setTotalPodcastEpisodesCount"
    | "setIsPodcastEpisodesInitialLoading"
    | "setPodcastEpisodesSearchTerm"
    | "resetPodcastEpisodesTableState"
    | "setIsPodcastExcludeModalOpen"
    | "setPendingPodcastExclusionIds"
    | "resetPodcastExcludeModalState"
    | "setSelectedPodcastEpisodeIds"
    | "setPodcastExcludeSuccessCallback"
  >;

// Falls back to "No reason provided" as a defence-in-depth safeguard — schema validation should prevent empty submissions.
const buildReasonString = (reasons: ExcludeItemReasons): string => {
  const parts: string[] = [];
  if (reasons.notRelevant) parts.push("This is not relevant to the company");
  if (reasons.other && reasons.otherReason) parts.push(reasons.otherReason);
  return parts.join(", ") || "No reason provided";
};

export const createDiscoveryContentManagementActions: StateCreator<
  DiscoveryArticlesTableSlice &
    DiscoveryNarrativeClustersTableSlice &
    DiscoveryArticlesTableModalState &
    DiscoveryBroadcastClipsTableSlice &
    DiscoveryPodcastEpisodesTableSlice,
  [["zustand/devtools", never]],
  [],
  DiscoveryContentManagementActions
> = (set, get) => ({
  // ---------------------------------------------------------------------------
  // Exclusion async actions
  // ---------------------------------------------------------------------------

  excludeBroadcastClips: async (feedId, reasons, callbacks) => {
    const {
      pendingBroadcastExclusionIds,
      broadcastExcludeSuccessCallback,
      resetBroadcastExcludeModalState,
      setSelectedBroadcastClipIds,
    } = get();

    if (pendingBroadcastExclusionIds.length === 0) {
      return { error: "No clips selected for exclusion" };
    }

    const reason = buildReasonString(reasons);
    try {
      const result = await client
        .mutation(ExcludeBroadcastClips, {
          clipIds: pendingBroadcastExclusionIds,
          feedId,
          reason,
        })
        .toPromise();

      if (result.error) {
        console.error(
          "Failed to exclude broadcast clips:",
          result.error.message,
        );
        return { error: result.error.message };
      }

      // OperationInfo = backend validation error; treat as failure even though result.error is undefined.
      const responseData = result.data?.excludeBroadcastClips;
      if (responseData?.__typename === "OperationInfo") {
        const msg = responseData.messages?.[0]?.message ?? "Exclusion failed";
        console.error(
          "Failed to exclude broadcast clips (OperationInfo):",
          msg,
        );
        return { error: msg };
      }

      resetBroadcastExcludeModalState();
      setSelectedBroadcastClipIds([]);
      (
        callbacks?.onSuccess ??
        broadcastExcludeSuccessCallback ??
        (() => undefined)
      )();
      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to exclude broadcast clips:", err);
      return { error: msg };
    }
  },

  excludePodcastEpisodes: async (feedId, reasons, callbacks) => {
    const {
      pendingPodcastExclusionIds,
      podcastExcludeSuccessCallback,
      resetPodcastExcludeModalState,
      setSelectedPodcastEpisodeIds,
    } = get();

    if (pendingPodcastExclusionIds.length === 0) {
      return { error: "No episodes selected for exclusion" };
    }

    const reason = buildReasonString(reasons);
    try {
      const result = await client
        .mutation(ExcludePodcastEpisodes, {
          episodeIds: pendingPodcastExclusionIds,
          feedId,
          reason,
        })
        .toPromise();

      if (result.error) {
        console.error(
          "Failed to exclude podcast episodes:",
          result.error.message,
        );
        return { error: result.error.message };
      }

      // OperationInfo = backend validation error; treat as failure even though result.error is undefined.
      const responseData = result.data?.excludePodcastEpisodes;
      if (responseData?.__typename === "OperationInfo") {
        const msg = responseData.messages?.[0]?.message ?? "Exclusion failed";
        console.error(
          "Failed to exclude podcast episodes (OperationInfo):",
          msg,
        );
        return { error: msg };
      }

      resetPodcastExcludeModalState();
      setSelectedPodcastEpisodeIds([]);
      (
        callbacks?.onSuccess ??
        podcastExcludeSuccessCallback ??
        (() => undefined)
      )();
      return { error: null };
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      console.error("Failed to exclude podcast episodes:", err);
      return { error: msg };
    }
  },

  // ---------------------------------------------------------------------------
  // Articles Table
  // ---------------------------------------------------------------------------

  setInitialLoading: (loading: boolean) => {
    set(
      { isInitialLoading: loading },
      false,
      "[User] Discovery Content Management: Set initial loading state",
    );
  },

  setFetchingArticles: (fetching: boolean) => {
    set(
      { isFetchingArticles: fetching },
      false,
      "[User] Discovery Content Management: Set fetching state",
    );
  },

  setError: (error: string | null) => {
    set(
      { error },
      false,
      "[User] Discovery Content Management: Set error state",
    );
  },

  setTotalCount: (totalCount: number) => {
    set(
      { totalCount },
      false,
      "[User] Discovery Content Management: Set total count",
    );
  },

  setSearchTerm: (searchTerm: string) => {
    const { search } = get();
    set(
      {
        search: {
          ...search,
          searchTerm,
        },
      },
      false,
      "[User] Discovery Content Management: Set search term",
    );
  },

  setIsAdvancedSearch: (isAdvancedSearch: boolean) => {
    const { search } = get();
    set(
      {
        search: {
          ...search,
          isAdvancedSearch,
        },
      },
      false,
      "[User] Discovery Content Management: Set advanced search",
    );
  },

  resetState: () => {
    const { search } = get();
    set(
      {
        isInitialLoading: false,
        isFetchingArticles: false,
        error: null,
        totalCount: 0,
        search, // Preserve search state (searchTerm and isAdvancedSearch)
      },
      false,
      "[User] Discovery Content Management: Reset state",
    );
  },

  // ---------------------------------------------------------------------------
  // Narrative Clusters Table
  // ---------------------------------------------------------------------------

  setIsNarrativeClustersInitialLoading: (loading: boolean) => {
    set(
      { isNarrativeClustersInitialLoading: loading },
      false,
      "[User] Discovery Content Management: Set narrative clusters initial loading state",
    );
  },

  setIsFetchingNarrativeClustersTableData: (fetching: boolean) => {
    set(
      { isFetchingNarrativeClustersTableData: fetching },
      false,
      "[User] Discovery Content Management: Set narrative clusters table data fetching state",
    );
  },

  setNarrativeClustersTableError: (error: string | null) => {
    set(
      { narrativeClustersTableError: error },
      false,
      "[User] Discovery Content Management: Set narrative clusters table error state",
    );
  },

  setTotalNarrativeClustersCount: (totalCount: number) => {
    set(
      { totalNarrativeClustersCount: totalCount },
      false,
      "[User] Discovery Content Management: Set total narrative clusters count",
    );
  },

  setNarrativeClustersSearchTerm: (searchTerm: string) => {
    set(
      { narrativeClustersSearchTerm: searchTerm },
      false,
      "[User] Discovery Content Management: Set narrative clusters search term",
    );
  },

  resetNarrativeClustersTableState: () => {
    set(
      {
        isNarrativeClustersInitialLoading: false,
        isFetchingNarrativeClustersTableData: false,
        narrativeClustersTableError: null,
        totalNarrativeClustersCount: 0,
        narrativeClustersSearchTerm: "",
      },
      false,
      "[User] Discovery Content Management: Reset narrative clusters table state",
    );
  },

  // ---------------------------------------------------------------------------
  // Articles Table Modal
  // ---------------------------------------------------------------------------

  setIsAddArticleModalOpen: (isOpen: boolean) => {
    set(
      { isAddArticleModalOpen: isOpen },
      false,
      "[User] Discovery Content Management: Set add article modal open",
    );
  },

  setIsAddingArticle: (adding: boolean) => {
    set(
      { isAddingArticle: adding },
      false,
      "[User] Discovery Content Management: Set adding article",
    );
  },

  resetAddArticleModalState: () => {
    set(
      {
        isAddArticleModalOpen: false,
        isAddingArticle: false,
        previewArticle: null,
      },
      false,
      "[User] Discovery Content Management: Reset add article modal state",
    );
  },

  setPreviewArticle: (article: FeedArticleProjectionData | null) => {
    set(
      { previewArticle: article },
      false,
      "[User] Discovery Content Management: Set preview article",
    );
  },

  // ---------------------------------------------------------------------------
  // Broadcast Clips Table
  // ---------------------------------------------------------------------------

  setIsFetchingBroadcastClips: (isFetching: boolean) => {
    set(
      {
        isFetchingBroadcastClips: isFetching,
        ...(isFetching ? { isBroadcastClipsInitialLoading: false } : {}),
      },
      false,
      "[User] Discovery Content Management: Set broadcast clips fetching state",
    );
  },

  setBroadcastClipsError: (error: string | null) => {
    set(
      { broadcastClipsError: error },
      false,
      "[User] Discovery Content Management: Set broadcast clips error",
    );
  },

  setTotalBroadcastClipsCount: (count: number) => {
    set(
      { totalBroadcastClipsCount: count },
      false,
      "[User] Discovery Content Management: Set total broadcast clips count",
    );
  },

  setIsBroadcastClipsInitialLoading: (isLoading: boolean) => {
    set(
      { isBroadcastClipsInitialLoading: isLoading },
      false,
      "[User] Discovery Content Management: Set broadcast clips initial loading state",
    );
  },

  setBroadcastClipsSearchTerm: (searchTerm: string) => {
    set(
      { broadcastClipsSearchTerm: searchTerm },
      false,
      "[User] Discovery Content Management: Set broadcast clips search term",
    );
  },

  resetBroadcastClipsTableState: () => {
    set(
      {
        isFetchingBroadcastClips: false,
        isBroadcastClipsInitialLoading: false,
        totalBroadcastClipsCount: 0,
        broadcastClipsError: null,
        selectedBroadcastClipIds: [], // prevent stale IDs in the Delete (N) button on re-navigation
      },
      false,
      "[User] Discovery Content Management: Reset broadcast clips table state",
    );
  },

  setBroadcastExcludeSuccessCallback: (cb) => {
    set({ broadcastExcludeSuccessCallback: cb });
  },

  setIsBroadcastExcludeModalOpen: (isOpen) => {
    set(
      { isBroadcastExcludeModalOpen: isOpen },
      false,
      "[User] Discovery Content Management: Set broadcast exclude modal open",
    );
  },

  setPendingBroadcastExclusionIds: (ids: string[]) => {
    set(
      { pendingBroadcastExclusionIds: ids },
      false,
      "[User] Discovery Content Management: Set pending broadcast exclusion ids",
    );
  },

  resetBroadcastExcludeModalState: () => {
    set(
      { isBroadcastExcludeModalOpen: false, pendingBroadcastExclusionIds: [] },
      false,
      "[User] Discovery Content Management: Reset broadcast exclude modal state",
    );
  },

  setSelectedBroadcastClipIds: (ids: string[]) => {
    set(
      { selectedBroadcastClipIds: ids },
      false,
      "[User] Discovery Content Management: Set selected broadcast clip ids",
    );
  },

  // ---------------------------------------------------------------------------
  // Podcast Episodes Table
  // ---------------------------------------------------------------------------

  setIsFetchingPodcastEpisodes: (isFetching: boolean) => {
    set(
      {
        isFetchingPodcastEpisodes: isFetching,
        ...(isFetching ? { isPodcastEpisodesInitialLoading: false } : {}),
      },
      false,
      "[User] Discovery Content Management: Set podcast episodes fetching state",
    );
  },

  setPodcastEpisodesError: (error: string | null) => {
    set(
      { podcastEpisodesError: error },
      false,
      "[User] Discovery Content Management: Set podcast episodes error",
    );
  },

  setTotalPodcastEpisodesCount: (count: number) => {
    set(
      { totalPodcastEpisodesCount: count },
      false,
      "[User] Discovery Content Management: Set total podcast episodes count",
    );
  },

  setIsPodcastEpisodesInitialLoading: (isLoading: boolean) => {
    set(
      { isPodcastEpisodesInitialLoading: isLoading },
      false,
      "[User] Discovery Content Management: Set podcast episodes initial loading state",
    );
  },

  setPodcastEpisodesSearchTerm: (searchTerm: string) => {
    set(
      { podcastEpisodesSearchTerm: searchTerm },
      false,
      "[User] Discovery Content Management: Set podcast episodes search term",
    );
  },

  resetPodcastEpisodesTableState: () => {
    set(
      {
        isFetchingPodcastEpisodes: false,
        isPodcastEpisodesInitialLoading: false,
        totalPodcastEpisodesCount: 0,
        podcastEpisodesError: null,
        selectedPodcastEpisodeIds: [], // prevent stale IDs in the Delete (N) button on re-navigation
      },
      false,
      "[User] Discovery Content Management: Reset podcast episodes table state",
    );
  },

  setPodcastExcludeSuccessCallback: (cb) => {
    set({ podcastExcludeSuccessCallback: cb });
  },

  setIsPodcastExcludeModalOpen: (isOpen) => {
    set(
      { isPodcastExcludeModalOpen: isOpen },
      false,
      "[User] Discovery Content Management: Set podcast exclude modal open",
    );
  },

  setPendingPodcastExclusionIds: (ids: string[]) => {
    set(
      { pendingPodcastExclusionIds: ids },
      false,
      "[User] Discovery Content Management: Set pending podcast exclusion ids",
    );
  },

  resetPodcastExcludeModalState: () => {
    set(
      { isPodcastExcludeModalOpen: false, pendingPodcastExclusionIds: [] },
      false,
      "[User] Discovery Content Management: Reset podcast exclude modal state",
    );
  },

  setSelectedPodcastEpisodeIds: (ids: string[]) => {
    set(
      { selectedPodcastEpisodeIds: ids },
      false,
      "[User] Discovery Content Management: Set selected podcast episode ids",
    );
  },
});
